import * as THREE from 'three';
import { Entity } from './Entity.js';
import { CONFIG, ASCII_CHARS } from '../core/config.js';
import { EnemyBullet } from './EnemyBullet.js';

export class Enemy extends Entity {
    constructor(x, y, type, scene, difficultyMultiplier = 1) {
        const config = Enemy.getTypeConfig(type);
        super(x, y, config.char, config.color, scene);
        
        this.type = type;
        this.health = config.health;
        this.maxHealth = config.health;
        this.speed = config.speed * (0.9 + Math.random() * 0.2) * Math.min(difficultyMultiplier, 2.5);
        this.damage = config.damage;
        this.expValue = config.exp;
        this.attackCooldown = 0;
        this.stunned = 0;
        this.shootCooldown = 0;
        this.splitOnDeath = config.splitOnDeath || false;
        this.splitCount = config.splitCount || 0;
        
        // New special properties
        this.teleportCooldown = 0;
        this.healCooldown = 0;
        this.spawnCooldown = 0;
        this.shieldActive = false;
        this.berserkerMode = false;
        this.originalSpeed = this.speed;
        this.originalDamage = this.damage;
        
        // Shield enemy starts with shield
        if (type === 'shield') {
            this.shieldActive = true;
            this.damageReduction = 0.5; // 50% damage reduction when shielded
        }
    }
    
    static getTypeConfig(type) {
        switch(type) {
            case 'fast':
                return {
                    char: ASCII_CHARS.FAST_ENEMY,
                    color: CONFIG.COLORS.FAST_ENEMY,
                    health: 15,
                    speed: 4.0,
                    damage: 8,
                    exp: 1
                };
            case 'tank':
                return {
                    char: ASCII_CHARS.TANK_ENEMY,
                    color: CONFIG.COLORS.TANK_ENEMY,
                    health: 60,
                    speed: 1.0,
                    damage: 25,
                    exp: 4
                };
            case 'elite':
                return {
                    char: ASCII_CHARS.ELITE,
                    color: CONFIG.COLORS.ELITE_ENEMY,
                    health: 45,
                    speed: 2.5,
                    damage: 15,
                    exp: 3
                };
            case 'splitter':
                return {
                    char: ASCII_CHARS.SPLITTER,
                    color: CONFIG.COLORS.SPLITTER_ENEMY,
                    health: 25,
                    speed: 2.0,
                    damage: 10,
                    exp: 2,
                    splitOnDeath: true,
                    splitCount: 3
                };
            case 'ranged':
                return {
                    char: ASCII_CHARS.RANGED,
                    color: CONFIG.COLORS.RANGED_ENEMY,
                    health: 20,
                    speed: 1.5,
                    damage: 12,
                    exp: 2
                };
            case 'swarm':
                return {
                    char: ASCII_CHARS.SWARM,
                    color: CONFIG.COLORS.SWARM_ENEMY,
                    health: 8,
                    speed: 3.5,
                    damage: 4,
                    exp: 1
                };
            case 'teleporter':
                return {
                    char: ASCII_CHARS.TELEPORTER,
                    color: CONFIG.COLORS.TELEPORTER_ENEMY,
                    health: 30,
                    speed: 1.5,
                    damage: 12,
                    exp: 3
                };
            case 'shield':
                return {
                    char: ASCII_CHARS.SHIELD,
                    color: CONFIG.COLORS.SHIELD_ENEMY,
                    health: 50,
                    speed: 1.2,
                    damage: 8,
                    exp: 3
                };
            case 'healer':
                return {
                    char: ASCII_CHARS.HEALER,
                    color: CONFIG.COLORS.HEALER_ENEMY,
                    health: 25,
                    speed: 1.0,
                    damage: 5,
                    exp: 4
                };
            case 'berserker':
                return {
                    char: ASCII_CHARS.BERSERKER,
                    color: CONFIG.COLORS.BERSERKER_ENEMY,
                    health: 40,
                    speed: 3.0,
                    damage: 18,
                    exp: 3
                };
            case 'spawner':
                return {
                    char: ASCII_CHARS.SPAWNER,
                    color: CONFIG.COLORS.SPAWNER_ENEMY,
                    health: 35,
                    speed: 0.8,
                    damage: 6,
                    exp: 5
                };
            case 'mini_boss':
                return {
                    char: ASCII_CHARS.MINI_BOSS,
                    color: CONFIG.COLORS.MINI_BOSS,
                    health: 120,
                    speed: 1.8,
                    damage: 30,
                    exp: 8
                };
            case 'phantom':
                return {
                    char: ASCII_CHARS.PHANTOM,
                    color: CONFIG.COLORS.PHANTOM_ENEMY,
                    health: 35,
                    speed: 2.2,
                    damage: 14,
                    exp: 3
                };
            case 'virus':
                return {
                    char: ASCII_CHARS.VIRUS,
                    color: CONFIG.COLORS.VIRUS_ENEMY,
                    health: 28,
                    speed: 1.8,
                    damage: 12,
                    exp: 3
                };
            case 'firewall':
                return {
                    char: ASCII_CHARS.FIREWALL,
                    color: CONFIG.COLORS.FIREWALL_ENEMY,
                    health: 80,
                    speed: 0.5,
                    damage: 20,
                    exp: 4
                };
            case 'malware':
                return {
                    char: ASCII_CHARS.MALWARE,
                    color: CONFIG.COLORS.MALWARE_ENEMY,
                    health: 40,
                    speed: 2.0,
                    damage: 16,
                    exp: 3
                };
            default: // basic
                return {
                    char: ASCII_CHARS.ENEMY,
                    color: CONFIG.COLORS.ENEMY,
                    health: 22,
                    speed: 2.5,
                    damage: 10,
                    exp: 1
                };
        }
    }
    
    update(deltaTime, camera, gameState) {
        super.update(deltaTime, camera);
        
        this.attackCooldown -= deltaTime;
        this.stunned -= deltaTime;
        this.shootCooldown -= deltaTime;
        this.teleportCooldown -= deltaTime;
        this.healCooldown -= deltaTime;
        this.spawnCooldown -= deltaTime;
        
        if (this.stunned <= 0) {
            this.updateAI(deltaTime, gameState);
        }
    }
    
    updateAI(deltaTime, gameState) {
        const player = gameState.player;
        if (!player) return;
        
        const distToPlayer = this.getDistanceTo(player);
        
        // Type-specific behavior
        switch(this.type) {
            case 'ranged':
                this.updateRangedAI(deltaTime, gameState, player, distToPlayer);
                break;
            case 'swarm':
                this.updateSwarmAI(deltaTime, gameState, player, distToPlayer);
                break;
            case 'mini_boss':
                this.updateMiniBossAI(deltaTime, gameState, player, distToPlayer);
                break;
            case 'teleporter':
                this.updateTeleporterAI(deltaTime, gameState, player, distToPlayer);
                break;
            case 'shield':
                this.updateShieldAI(deltaTime, gameState, player, distToPlayer);
                break;
            case 'healer':
                this.updateHealerAI(deltaTime, gameState, player, distToPlayer);
                break;
            case 'berserker':
                this.updateBerserkerAI(deltaTime, gameState, player, distToPlayer);
                break;
            case 'spawner':
                this.updateSpawnerAI(deltaTime, gameState, player, distToPlayer);
                break;
            case 'phantom':
                this.updatePhantomAI(deltaTime, gameState, player, distToPlayer);
                break;
            case 'virus':
                this.updateVirusAI(deltaTime, gameState, player, distToPlayer);
                break;
            case 'firewall':
                this.updateFirewallAI(deltaTime, gameState, player, distToPlayer);
                break;
            case 'malware':
                this.updateMalwareAI(deltaTime, gameState, player, distToPlayer);
                break;
            default:
                this.updateBasicAI(deltaTime, gameState, player, distToPlayer);
                break;
        }
    }
    
    updateBasicAI(deltaTime, gameState, player, distToPlayer) {
        // Smart AI - move toward player with enemy avoidance
        const direction = this.getDirectionTo(player);
        
        // Add avoidance of other enemies to prevent clustering
        let avoidance = new THREE.Vector3();
        gameState.enemies.forEach(other => {
            if (other !== this) {
                const dist = this.getDistanceTo(other);
                if (dist < 2) {
                    const avoid = new THREE.Vector3()
                        .subVectors(this.position, other.position)
                        .normalize()
                        .multiplyScalar(0.3);
                    avoidance.add(avoid);
                }
            }
        });
        
        direction.add(avoidance);
        direction.normalize();
        
        // Move toward player
        this.position.add(direction.multiplyScalar(this.speed * deltaTime));
        
        // Attack if close enough
        if (distToPlayer < 1.5 && this.attackCooldown <= 0) {
            this.attackPlayer(player, gameState);
            this.attackCooldown = 1.5;
        }
    }
    
    updateRangedAI(deltaTime, gameState, player, distToPlayer) {
        // Keep distance and shoot
        const optimalDistance = 8;
        const direction = this.getDirectionTo(player);
        
        if (distToPlayer < optimalDistance) {
            // Move away to maintain distance
            direction.multiplyScalar(-1);
            this.position.add(direction.multiplyScalar(this.speed * deltaTime));
        } else if (distToPlayer > optimalDistance + 2) {
            // Move closer if too far
            this.position.add(direction.multiplyScalar(this.speed * deltaTime * 0.5));
        }
        
        // Shoot at player
        if (distToPlayer < 12 && this.shootCooldown <= 0) {
            this.shootAtPlayer(player, gameState);
            this.shootCooldown = 2.0;
        }
    }
    
    updateSwarmAI(deltaTime, gameState, player, distToPlayer) {
        // Fast, direct movement with less avoidance
        const direction = this.getDirectionTo(player);
        this.position.add(direction.multiplyScalar(this.speed * deltaTime));
        
        // Quick attacks
        if (distToPlayer < 1.2 && this.attackCooldown <= 0) {
            this.attackPlayer(player, gameState);
            this.attackCooldown = 1.0;
        }
    }
    
    updateMiniBossAI(deltaTime, gameState, player, distToPlayer) {
        // Complex AI with multiple attacks
        const direction = this.getDirectionTo(player);
        this.position.add(direction.multiplyScalar(this.speed * deltaTime));
        
        // Melee attack
        if (distToPlayer < 2.0 && this.attackCooldown <= 0) {
            this.attackPlayer(player, gameState);
            this.attackCooldown = 2.0;
        }
        
        // Ranged attack
        if (distToPlayer < 15 && this.shootCooldown <= 0) {
            this.shootBurst(player, gameState);
            this.shootCooldown = 3.0;
        }
    }
    
    shootAtPlayer(player, gameState) {
        const bullet = new EnemyBullet(
            this.position.x,
            this.position.z,
            player.position.x,
            player.position.z,
            this.damage * 0.7, // Reduced damage for ranged attacks
            this.scene
        );
        gameState.enemyBullets = gameState.enemyBullets || [];
        gameState.enemyBullets.push(bullet);
    }
    
    shootBurst(player, gameState) {
        // Shoot 3 bullets in a spread pattern
        for (let i = 0; i < 3; i++) {
            const angle = (i - 1) * 0.3; // Spread pattern
            const direction = this.getDirectionTo(player);
            const rotatedDirection = new THREE.Vector3(
                direction.x * Math.cos(angle) - direction.z * Math.sin(angle),
                0,
                direction.x * Math.sin(angle) + direction.z * Math.cos(angle)
            );
            
            const targetX = this.position.x + rotatedDirection.x * 20;
            const targetY = this.position.z + rotatedDirection.z * 20;
            
            const bullet = new EnemyBullet(
                this.position.x,
                this.position.z,
                targetX,
                targetY,
                this.damage * 0.5,
                this.scene
            );
            
            gameState.enemyBullets = gameState.enemyBullets || [];
            gameState.enemyBullets.push(bullet);
        }
    }
    
    attackPlayer(player, gameState) {
        if (player.takeDamage(this.damage)) {
            // Player died
            gameState.gameOver = true;
        } else {
            gameState.stats.health = Math.max(0, gameState.stats.health - this.damage);
        }
    }
    
    onDeath(gameState) {
        if (this.splitOnDeath) {
            this.spawnSplits(gameState);
        }
    }
    
    spawnSplits(gameState) {
        for (let i = 0; i < this.splitCount; i++) {
            const angle = (Math.PI * 2 * i) / this.splitCount;
            const distance = 2;
            const x = this.position.x + Math.cos(angle) * distance;
            const y = this.position.z + Math.sin(angle) * distance;
            
            const splitEnemy = new Enemy(x, y, 'swarm', this.scene, 1);
            splitEnemy.health = 10;
            splitEnemy.maxHealth = 10;
            gameState.enemies.push(splitEnemy);
        }
    }
    
    stun(duration) {
        this.stunned = Math.max(this.stunned, duration);
    }
    
    updateTeleporterAI(deltaTime, gameState, player, distToPlayer) {
        // Teleport when too close or far, otherwise move normally
        if (distToPlayer < 3 && this.teleportCooldown <= 0) {
            // Teleport away from player
            this.teleportAway(player, gameState);
            this.teleportCooldown = 4.0;
        } else if (distToPlayer > 12 && this.teleportCooldown <= 0) {
            // Teleport closer to player
            this.teleportToPlayer(player, gameState);
            this.teleportCooldown = 5.0;
        } else {
            // Normal movement
            const direction = this.getDirectionTo(player);
            this.position.add(direction.multiplyScalar(this.speed * deltaTime));
        }
        
        // Attack if close enough
        if (distToPlayer < 2.0 && this.attackCooldown <= 0) {
            this.attackPlayer(player, gameState);
            this.attackCooldown = 1.8;
        }
    }
    
    updateShieldAI(deltaTime, gameState, player, distToPlayer) {
        // Toggle shield based on health
        if (this.health < this.maxHealth * 0.5 && !this.shieldActive) {
            this.activateShield();
        } else if (this.health > this.maxHealth * 0.8 && this.shieldActive) {
            this.deactivateShield();
        }
        
        // Move toward player
        const direction = this.getDirectionTo(player);
        this.position.add(direction.multiplyScalar(this.speed * deltaTime));
        
        // Attack if close enough
        if (distToPlayer < 1.8 && this.attackCooldown <= 0) {
            this.attackPlayer(player, gameState);
            this.attackCooldown = 2.0;
        }
    }
    
    updateHealerAI(deltaTime, gameState, player, distToPlayer) {
        // Stay back and heal other enemies
        if (distToPlayer < 6) {
            // Move away from player
            const direction = this.getDirectionTo(player);
            direction.multiplyScalar(-1);
            this.position.add(direction.multiplyScalar(this.speed * deltaTime));
        }
        
        // Heal nearby damaged enemies
        if (this.healCooldown <= 0) {
            this.healNearbyEnemies(gameState);
            this.healCooldown = 3.0;
        }
        
        // Defend self if player is too close
        if (distToPlayer < 2.0 && this.attackCooldown <= 0) {
            this.attackPlayer(player, gameState);
            this.attackCooldown = 2.5;
        }
    }
    
    updateBerserkerAI(deltaTime, gameState, player, distToPlayer) {
        // Enter berserker mode when health is low
        if (this.health < this.maxHealth * 0.4 && !this.berserkerMode) {
            this.enterBerserkerMode();
        }
        
        // Aggressive movement toward player
        const direction = this.getDirectionTo(player);
        this.position.add(direction.multiplyScalar(this.speed * deltaTime));
        
        // Frequent attacks
        if (distToPlayer < 2.0 && this.attackCooldown <= 0) {
            this.attackPlayer(player, gameState);
            this.attackCooldown = this.berserkerMode ? 0.8 : 1.5;
        }
    }
    
    updateSpawnerAI(deltaTime, gameState, player, distToPlayer) {
        // Stay back and spawn minions
        if (distToPlayer < 8) {
            // Move away from player
            const direction = this.getDirectionTo(player);
            direction.multiplyScalar(-1);
            this.position.add(direction.multiplyScalar(this.speed * deltaTime));
        }
        
        // Spawn minions
        if (this.spawnCooldown <= 0 && gameState.enemies.length < 250) {
            this.spawnMinion(gameState);
            this.spawnCooldown = 6.0;
        }
        
        // Defend self if necessary
        if (distToPlayer < 3.0 && this.attackCooldown <= 0) {
            this.attackPlayer(player, gameState);
            this.attackCooldown = 2.0;
        }
    }
    
    // Special ability methods for new enemy types
    teleportAway(player, gameState) {
        const angle = Math.random() * Math.PI * 2;
        const distance = 8 + Math.random() * 4;
        const newX = player.position.x + Math.cos(angle) * distance;
        const newZ = player.position.z + Math.sin(angle) * distance;
        
        this.position.set(newX, 0, newZ);
        
        // Create teleport effect at old and new positions
        if (window.gameParticles) {
            window.gameParticles.createPickupEffect(this.position.x, this.position.z);
        }
    }
    
    teleportToPlayer(player, gameState) {
        const angle = Math.random() * Math.PI * 2;
        const distance = 4 + Math.random() * 2;
        const newX = player.position.x + Math.cos(angle) * distance;
        const newZ = player.position.z + Math.sin(angle) * distance;
        
        this.position.set(newX, 0, newZ);
        
        // Create teleport effect
        if (window.gameParticles) {
            window.gameParticles.createPickupEffect(this.position.x, this.position.z);
        }
    }
    
    activateShield() {
        this.shieldActive = true;
        this.damageReduction = 0.5;
        // Visual effect - make enemy more opaque/glowing
        this.glowIntensity = 2.0;
    }
    
    deactivateShield() {
        this.shieldActive = false;
        this.damageReduction = 0;
        this.glowIntensity = 1.0;
    }
    
    healNearbyEnemies(gameState) {
        const healRange = 5;
        const healAmount = 15;
        let healed = false;
        
        gameState.enemies.forEach(enemy => {
            if (enemy !== this && enemy.health < enemy.maxHealth) {
                const dist = this.getDistanceTo(enemy);
                if (dist < healRange) {
                    enemy.health = Math.min(enemy.maxHealth, enemy.health + healAmount);
                    healed = true;
                    
                    // Visual heal effect
                    if (window.gameParticles) {
                        window.gameParticles.createPickupEffect(enemy.position.x, enemy.position.z);
                    }
                }
            }
        });
        
        return healed;
    }
    
    enterBerserkerMode() {
        this.berserkerMode = true;
        this.speed = this.originalSpeed * 1.5;
        this.damage = this.originalDamage * 1.3;
        // Visual effect - red tint
        this.color = 0xff2222;
        this.glowIntensity = 1.5;
    }
    
    spawnMinion(gameState) {
        const angle = Math.random() * Math.PI * 2;
        const distance = 2;
        const x = this.position.x + Math.cos(angle) * distance;
        const y = this.position.z + Math.sin(angle) * distance;
        
        const minion = new Enemy(x, y, 'swarm', this.scene, 0.8);
        minion.health = 6;
        minion.maxHealth = 6;
        gameState.enemies.push(minion);
        
        // Spawn effect
        if (window.gameParticles) {
            window.gameParticles.createDeathEffect(x, y, 'spawner');
        }
    }
    
    takeDamage(amount, isCritical = false) {
        // Apply shield damage reduction
        if (this.shieldActive && this.damageReduction) {
            amount *= (1 - this.damageReduction);
        }
        
        return super.takeDamage(amount, isCritical);
    }
    
    // New enemy AI behaviors
    updatePhantomAI(deltaTime, gameState, player, distToPlayer) {
        // Phantom can phase through other enemies and has erratic movement
        const direction = this.getDirectionTo(player);
        
        // Add random movement component for erratic behavior
        const randomAngle = Math.random() * Math.PI * 2;
        const randomComponent = new THREE.Vector3(
            Math.cos(randomAngle) * 0.3,
            0,
            Math.sin(randomAngle) * 0.3
        );
        
        direction.add(randomComponent);
        direction.normalize();
        
        this.position.add(direction.multiplyScalar(this.speed * deltaTime));
        
        // Quick phase attacks
        if (distToPlayer < 1.5 && this.attackCooldown <= 0) {
            this.attackPlayer(player, gameState);
            this.attackCooldown = 1.2;
        }
    }
    
    updateVirusAI(deltaTime, gameState, player, distToPlayer) {
        // Virus spreads corruption to nearby enemies, making them stronger
        const direction = this.getDirectionTo(player);
        this.position.add(direction.multiplyScalar(this.speed * deltaTime));
        
        // Spread virus effect to nearby enemies
        if (this.healCooldown <= 0) {
            this.spreadVirus(gameState);
            this.healCooldown = 4.0;
        }
        
        // Attack player
        if (distToPlayer < 1.8 && this.attackCooldown <= 0) {
            this.attackPlayer(player, gameState);
            this.attackCooldown = 1.5;
        }
    }
    
    updateFirewallAI(deltaTime, gameState, player, distToPlayer) {
        // Firewall moves slowly but blocks projectiles and has high defense
        if (distToPlayer > 3) {
            // Only move if far from player
            const direction = this.getDirectionTo(player);
            this.position.add(direction.multiplyScalar(this.speed * deltaTime));
        }
        
        // Strong defensive attack
        if (distToPlayer < 2.5 && this.attackCooldown <= 0) {
            this.attackPlayer(player, gameState);
            this.attackCooldown = 2.5;
        }
    }
    
    updateMalwareAI(deltaTime, gameState, player, distToPlayer) {
        // Malware corrupts the environment and has aggressive behavior
        const direction = this.getDirectionTo(player);
        this.position.add(direction.multiplyScalar(this.speed * deltaTime));
        
        // Frequent ranged attacks
        if (distToPlayer < 10 && this.shootCooldown <= 0) {
            this.shootAtPlayer(player, gameState);
            this.shootCooldown = 1.5;
        }
        
        // Melee attack
        if (distToPlayer < 1.5 && this.attackCooldown <= 0) {
            this.attackPlayer(player, gameState);
            this.attackCooldown = 1.0;
        }
    }
    
    spreadVirus(gameState) {
        const virusRange = 4;
        let infected = false;
        
        gameState.enemies.forEach(enemy => {
            if (enemy !== this && enemy.type !== 'virus') {
                const dist = this.getDistanceTo(enemy);
                if (dist < virusRange) {
                    // Boost enemy stats temporarily
                    enemy.speed *= 1.1;
                    enemy.damage *= 1.1;
                    infected = true;
                    
                    // Visual virus effect
                    if (window.gameParticles) {
                        window.gameParticles.createPickupEffect(enemy.position.x, enemy.position.z);
                    }
                }
            }
        });
        
        return infected;
    }
    
    static spawnRandomType(x, y, scene, gameTime, difficultyMultiplier) {
        let type = 'basic';
        const minutes = gameTime / 60;
        const rand = Math.random();
        
        // Progressive enemy type introduction based on game time
        if (minutes > 0.5 && rand < 0.10) type = 'fast';
        else if (minutes > 1 && rand < 0.16) type = 'swarm';
        else if (minutes > 1.5 && rand < 0.20) type = 'ranged';
        else if (minutes > 2 && rand < 0.24) type = 'elite';
        else if (minutes > 2.5 && rand < 0.26) type = 'splitter';
        else if (minutes > 3 && rand < 0.28) type = 'tank';
        else if (minutes > 3.5 && rand < 0.30) type = 'teleporter';
        else if (minutes > 4 && rand < 0.32) type = 'shield';
        else if (minutes > 4.5 && rand < 0.34) type = 'healer';
        else if (minutes > 5 && rand < 0.36) type = 'berserker';
        else if (minutes > 5.5 && rand < 0.38) type = 'spawner';
        else if (minutes > 6 && rand < 0.40) type = 'phantom';
        else if (minutes > 6.5 && rand < 0.42) type = 'virus';
        else if (minutes > 7 && rand < 0.44) type = 'firewall';
        else if (minutes > 7.5 && rand < 0.46) type = 'malware';
        else if (minutes > 8 && rand < 0.48) type = 'mini_boss';
        
        return new Enemy(x, y, type, scene, difficultyMultiplier);
    }
} 